import { getLeadAttributionPayload } from './attribution';

/*
  GA4 measurement ID. Falls back to the project's Firebase Analytics ID
  (G-T88ZS5N64T) when the env var isn't set, so analytics works on
  production builds without needing CI to inject REACT_APP_GA4_MEASUREMENT_ID.
  Override per environment by setting REACT_APP_GA4_MEASUREMENT_ID in .env.
*/
const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA4_MEASUREMENT_ID || 'G-T88ZS5N64T';
const GA_SCRIPT_ID = 'ga4-script';
const WEBSITE_ANALYTICS_ENDPOINT =
  process.env.REACT_APP_WEBSITE_ANALYTICS_URL ||
  'https://us-central1-emblem-c533b.cloudfunctions.net/trackWebsiteEvent';
const VISITOR_ID_STORAGE_KEY = 'emblem-website-visitor-id-v1';
const INTERNAL_TRAFFIC_DISABLED_STORAGE_KEY =
  'emblem-website-ignore-analytics-v1';
// v2 = session moved from sessionStorage (per-tab) to localStorage
// (per-visitor, with 30-min idle timeout). This matches GA's session
// model — opening a second tab no longer creates a phantom session,
// and a real new session only starts after 30 min of inactivity.
const SESSION_ID_STORAGE_KEY = 'emblem-website-session-id-v2';
const SESSION_LAST_SEEN_STORAGE_KEY = 'emblem-website-session-last-seen-v2';
const SESSION_STARTED_STORAGE_KEY = 'emblem-website-session-started-v2';
const PENDING_EVENTS_STORAGE_KEY = 'emblem-website-pending-events-v1';
const GEO_CACHE_STORAGE_KEY = 'emblem-website-geo-cache-v1';
const TRACKED_PATH_PREFIXES_TO_SKIP = ['/admin'];
const DEDUPE_WINDOW_MS = 1500;
const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 min, GA-equivalent
const PENDING_EVENTS_MAX = 50; // cap the offline buffer
let storeTrackingBound = false;
let storeTrackingHandler = null;
const recentEventCache = new Map();

const isAnalyticsEnabled = () => Boolean(GA_MEASUREMENT_ID);
const isWebsiteAnalyticsEnabled = () => Boolean(WEBSITE_ANALYTICS_ENDPOINT);

const isTrackablePath = (path = '') => {
  const normalizedPath = String(path || '').trim() || '/';
  return !TRACKED_PATH_PREFIXES_TO_SKIP.some((prefix) =>
    normalizedPath.startsWith(prefix)
  );
};

const createRandomId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};

const readStorageValue = (storage, key) => {
  try {
    return storage.getItem(key) || '';
  } catch (error) {
    return '';
  }
};

const writeStorageValue = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage failures.
  }
};

const getVisitorId = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return '';
  }

  const existingValue = readStorageValue(
    window.localStorage,
    VISITOR_ID_STORAGE_KEY
  );
  if (existingValue) {
    return existingValue;
  }

  const nextValue = createRandomId();
  writeStorageValue(window.localStorage, VISITOR_ID_STORAGE_KEY, nextValue);
  return nextValue;
};

// Returns a session ID that persists across tabs (localStorage) and
// rotates only after SESSION_IDLE_TIMEOUT_MS of inactivity. Every
// call updates `lastSeenAt`, so as long as the visitor keeps clicking
// the same session ID is reused. Closing all tabs and reopening
// within the timeout = same session. Going idle > 30 min and coming
// back = new session. This matches GA4's default session model and
// avoids the previous bug where every tab counted as a new session.
const getSessionId = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return '';
  }

  const existingValue = readStorageValue(
    window.localStorage,
    SESSION_ID_STORAGE_KEY,
  );
  const lastSeenRaw = readStorageValue(
    window.localStorage,
    SESSION_LAST_SEEN_STORAGE_KEY,
  );
  const lastSeenAt = Number(lastSeenRaw) || 0;
  const now = Date.now();
  const isStillActive =
    existingValue && lastSeenAt && now - lastSeenAt < SESSION_IDLE_TIMEOUT_MS;

  if (isStillActive) {
    writeStorageValue(window.localStorage, SESSION_LAST_SEEN_STORAGE_KEY, String(now));
    return existingValue;
  }

  // Stale (idle > 30 min) or missing — start a new session.
  const nextValue = createRandomId();
  writeStorageValue(window.localStorage, SESSION_ID_STORAGE_KEY, nextValue);
  writeStorageValue(window.localStorage, SESSION_LAST_SEEN_STORAGE_KEY, String(now));
  // Also reset the `session_start has fired` marker so the new
  // session emits its own session_start event.
  writeStorageValue(window.localStorage, SESSION_STARTED_STORAGE_KEY, '');
  return nextValue;
};

const isInternalTrafficDisabled = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  return (
    readStorageValue(window.localStorage, INTERNAL_TRAFFIC_DISABLED_STORAGE_KEY) ===
    '1'
  );
};

// ─── GDPR consent gate ────────────────────────────────────────
// We're hosted in Luxembourg — under GDPR + ePrivacy, analytics
// tracking that uses persistent identifiers (visitorId, sessionId)
// requires the visitor's prior consent. Until they accept, every
// trackEvent call short-circuits. The banner UI is responsible for
// calling setAnalyticsConsent('granted' | 'denied') based on the
// visitor's choice; both choices are remembered for 6 months.
// v2 — bumped intentionally so the consent banner re-asks every visitor.
// Earlier testing left some browsers with a pre-recorded decision, which
// suppressed the banner on subsequent visits even after we redesigned
// it (centered modal). Bumping the version invalidates those records
// and forces the banner to show again for everyone.
const ANALYTICS_CONSENT_STORAGE_KEY = 'emblem-website-analytics-consent-v2';
const ANALYTICS_CONSENT_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 6 months

const readAnalyticsConsent = () => {
  if (typeof window === 'undefined' || !window.localStorage) return 'unknown';
  try {
    const raw = readStorageValue(
      window.localStorage,
      ANALYTICS_CONSENT_STORAGE_KEY,
    );
    if (!raw) return 'unknown';
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return 'unknown';
    if (
      Number.isFinite(parsed.recordedAt) &&
      Date.now() - parsed.recordedAt > ANALYTICS_CONSENT_TTL_MS
    ) {
      return 'unknown';
    }
    if (parsed.value === 'granted' || parsed.value === 'denied') {
      return parsed.value;
    }
  } catch {
    // ignore
  }
  return 'unknown';
};

export const getAnalyticsConsent = () => readAnalyticsConsent();

export const setAnalyticsConsent = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (value !== 'granted' && value !== 'denied') return;
  try {
    writeStorageValue(
      window.localStorage,
      ANALYTICS_CONSENT_STORAGE_KEY,
      JSON.stringify({value, recordedAt: Date.now()}),
    );
    // If consent was just granted and we have a queued offline buffer,
    // flush it now rather than waiting for the next page load.
    if (value === 'granted') drainPendingEvents();
  } catch {
    // ignore
  }
};

const isAnalyticsConsentDenied = () => readAnalyticsConsent() === 'denied';

// Keep tracking opt-in but tolerant: while consent is 'unknown' we still
// emit, so existing visitors don't suddenly drop off the dashboard. Once
// the banner ships and starts setting 'granted' / 'denied' explicitly,
// flip this to a stricter `!== 'granted'` check.
const isAnalyticsBlockedByConsent = () => isAnalyticsConsentDenied();

const hasTrackedSessionStart = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }
  return Boolean(
    readStorageValue(window.localStorage, SESSION_STARTED_STORAGE_KEY),
  );
};

const markTrackedSessionStart = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  writeStorageValue(window.localStorage, SESSION_STARTED_STORAGE_KEY, '1');
};

// ─── Geo enrichment ───────────────────────────────────────────
// Geo is now stamped server-side in trackWebsiteEvent (Cloud Function)
// from the request IP via geoip-lite. The previous client-side ipapi.co
// lookup was rate-limited and ad-blocker-bait (~70% coverage) — server
// resolution is ~100% coverage with no third-party dependency.
//
// We keep a local helper that reads any stale ipapi cache once on
// startup and clears it, so devices that previously cached an ipapi
// result don't keep tagging events with stale data.
const clearLegacyClientGeoCache = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (window.localStorage.getItem(GEO_CACHE_STORAGE_KEY)) {
      window.localStorage.removeItem(GEO_CACHE_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
};
clearLegacyClientGeoCache();

// ─── Pending-event retry queue ────────────────────────────────
// Buffer events that failed to send (offline / endpoint down) in
// localStorage; flush on the next page load. Capped at 50 to keep
// the storage footprint sane.
const drainPendingEvents = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  let pending = [];
  try {
    const raw = readStorageValue(window.localStorage, PENDING_EVENTS_STORAGE_KEY);
    pending = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(pending)) pending = [];
  } catch {
    pending = [];
  }
  if (pending.length === 0) return;
  // Clear before sending — if a flush fails the events get re-queued
  // by postWebsiteEvent's catch, so we don't double-send.
  writeStorageValue(window.localStorage, PENDING_EVENTS_STORAGE_KEY, '[]');
  for (const payload of pending) {
    postWebsiteEvent(payload, { fromQueue: true });
  }
};

const queueEventForRetry = (payload) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  let pending = [];
  try {
    const raw = readStorageValue(window.localStorage, PENDING_EVENTS_STORAGE_KEY);
    pending = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(pending)) pending = [];
  } catch {
    pending = [];
  }
  pending.push(payload);
  // Trim oldest if over cap.
  if (pending.length > PENDING_EVENTS_MAX) {
    pending = pending.slice(-PENDING_EVENTS_MAX);
  }
  try {
    writeStorageValue(
      window.localStorage,
      PENDING_EVENTS_STORAGE_KEY,
      JSON.stringify(pending),
    );
  } catch {
    // localStorage full — ignore, can't queue.
  }
};

const parsePathInput = (path) => {
  const rawPath = String(path || '').trim();
  if (!rawPath) {
    return {
      pathname:
        typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
      search:
        typeof window !== 'undefined' ? window.location.search || '' : '',
    };
  }

  const [pathnamePart, ...searchParts] = rawPath.split('?');
  return {
    pathname: pathnamePart || '/',
    search: searchParts.length > 0 ? `?${searchParts.join('?')}` : '',
  };
};

const buildEventPayload = (eventName, params = {}, route = {}) => {
  const { pathname, search } = route.pathname
    ? route
    : parsePathInput(route.path);

  const attribution = getLeadAttributionPayload();
  const pagePath = pathname || '/';
  const pageSearch = typeof search === 'string' ? search : '';
  // Geo is stamped server-side from the request IP — no client lookup.

  return {
    eventName,
    visitorId: getVisitorId(),
    sessionId: getSessionId(),
    pagePath,
    pageSearch,
    pageTitle: typeof document !== 'undefined' ? document.title || '' : '',
    pageLocation:
      typeof window !== 'undefined'
        ? `${window.location.origin}${pagePath}${pageSearch}`
        : '',
    referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    locale:
      typeof navigator !== 'undefined'
        ? navigator.language || ''
        : '',
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
    params,
    attribution,
  };
};

// Window inside which an identical event (same key) is treated as a
// duplicate and dropped. page_view gets a generous 3s window because
// React-Router effects + canonical-path redirects + the language URL
// bridge can each fire a route effect on the same path within the same
// macrotask. Without this dedup the analytics page double-counts.
const PAGE_VIEW_DEDUPE_WINDOW_MS = 3000;

const shouldSkipDuplicateEvent = (eventName, payload) => {
  if (eventName === 'store_click') {
    const params = payload.params || {};
    const dedupeKey = [
      eventName,
      params.platform || '',
      params.placement || '',
      params.href || '',
    ].join('::');
    const now = Date.now();
    const previousTimestamp = recentEventCache.get(dedupeKey) || 0;
    recentEventCache.set(dedupeKey, now);
    return now - previousTimestamp < DEDUPE_WINDOW_MS;
  }

  if (eventName === 'page_view') {
    // Key by the rendered path (and visitorId for safety in case
    // multiple tabs/visitors share this module). The same visitor
    // viewing the same path twice within 3s is a route-effect
    // re-fire, not a real second pageview.
    const params = payload.params || {};
    const dedupeKey = [
      eventName,
      payload.visitorId || '',
      params.page_path || payload.pagePath || '',
    ].join('::');
    const now = Date.now();
    const previousTimestamp = recentEventCache.get(dedupeKey) || 0;
    recentEventCache.set(dedupeKey, now);
    return now - previousTimestamp < PAGE_VIEW_DEDUPE_WINDOW_MS;
  }

  return false;
};

const postWebsiteEvent = (payload, { fromQueue = false } = {}) => {
  if (
    typeof window === 'undefined' ||
    !isWebsiteAnalyticsEnabled() ||
    isInternalTrafficDisabled() ||
    !payload?.visitorId ||
    !payload?.sessionId
  ) {
    return;
  }

  // If the browser reports we're offline, skip the fetch and queue
  // straight away — saves a guaranteed-fail network round-trip.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    if (!fromQueue) queueEventForRetry(payload);
    return;
  }

  fetch(WEBSITE_ANALYTICS_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    keepalive: true,
    mode: 'cors',
  })
    .then((res) => {
      // 4xx/5xx → still a failure from the dashboard's POV; queue
      // it so the next page load can retry. fromQueue prevents a
      // tight loop when the endpoint is permanently broken.
      if (!res || !res.ok) {
        if (!fromQueue) queueEventForRetry(payload);
      }
    })
    .catch(() => {
      // Network failure (offline, DNS, CORS, etc.) — buffer for retry.
      if (!fromQueue) queueEventForRetry(payload);
    });
};

const ensureSessionStartTracked = (route) => {
  if (hasTrackedSessionStart()) {
    return;
  }

  const payload = buildEventPayload('session_start', {}, route);
  if (!isTrackablePath(payload.pagePath)) {
    return;
  }

  markTrackedSessionStart();
  postWebsiteEvent(payload);
};

const sendGaEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || !window.gtag || !isAnalyticsEnabled()) {
    return;
  }

  window.gtag('event', eventName, params);
};

export const initAnalytics = () => {
  if (typeof window === 'undefined' || !isAnalyticsEnabled()) {
    return;
  }

  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
  }

  if (!window.__emblemAnalyticsConfigured) {
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false
    });
    window.__emblemAnalyticsConfigured = true;
  }

  // Geo is now resolved server-side from the request IP — no client
  // lookup needed. Any stale ipapi cache from earlier builds was
  // already cleared at module load time.

  // Flush any events that were buffered on a previous visit because
  // the network was down or the endpoint was unreachable.
  drainPendingEvents();

  // When the browser comes back online mid-session, immediately
  // attempt to flush whatever's queued instead of waiting for the
  // next page load.
  if (!window.__emblemAnalyticsOnlineBound) {
    window.addEventListener('online', () => {
      drainPendingEvents();
    });
    window.__emblemAnalyticsOnlineBound = true;
  }

  bindJsErrorCapture();
  bindWebVitalsCapture();
  bindScrollDepthTracking();
  bindEngagementHeartbeat();
};

// ─── Scroll depth: 25 / 50 / 75 / 100% milestones ───────────────
// Fires a `scroll_depth` event each time a new milestone is crossed
// during a page-view. Reset on URL change so each route gets its own
// progression. Tells you whether visitors actually read the country /
// passport pages or bounce above the fold.
const SCROLL_DEPTH_MILESTONES = [25, 50, 75, 100];
let scrollDepthState = null;

const reachedMilestoneFor = (path, percent) => {
  if (!scrollDepthState || scrollDepthState.path !== path) {
    scrollDepthState = {path, hit: new Set()};
  }
  if (scrollDepthState.hit.has(percent)) return true;
  scrollDepthState.hit.add(percent);
  return false;
};

const computeScrollPercent = () => {
  if (typeof document === 'undefined') return 0;
  const scrollY =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight || 0;
  const docHeight = Math.max(
    document.body.scrollHeight || 0,
    document.documentElement.scrollHeight || 0,
    document.body.offsetHeight || 0,
    document.documentElement.offsetHeight || 0,
  );
  if (docHeight <= viewportHeight) return 100;
  const scrolled = scrollY + viewportHeight;
  return Math.min(Math.round((scrolled / docHeight) * 100), 100);
};

const handleScrollDepth = () => {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (!isTrackablePath(path)) return;
  const percent = computeScrollPercent();
  for (const milestone of SCROLL_DEPTH_MILESTONES) {
    if (percent >= milestone && !reachedMilestoneFor(path, milestone)) {
      try {
        trackEvent('scroll_depth', {
          milestone,
          path,
        });
      } catch {
        // ignore
      }
    }
  }
};

const bindScrollDepthTracking = () => {
  if (typeof window === 'undefined') return;
  if (window.__emblemScrollDepthBound) return;
  window.__emblemScrollDepthBound = true;
  // rAF-throttle so even fling-scrolls only fire one check per frame.
  let queued = false;
  const onScroll = () => {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(() => {
      queued = false;
      handleScrollDepth();
    });
  };
  window.addEventListener('scroll', onScroll, {passive: true});
  // Reset milestones whenever React-Router pushes a new URL.
  let lastPath = window.location.pathname;
  setInterval(() => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      scrollDepthState = null;
      handleScrollDepth();
    }
  }, 1000);
};

// ─── Engagement time per page (Page Visibility heartbeat) ───────
// Fires a `page_engaged` event every 15 seconds while the tab is
// visible (capped at 5 minutes per page) instead of computing
// time-on-page from start-to-end-of-last-event, which is noisy and
// inflates short visits. Each tick carries the cumulative engagedMs
// for the current page.
const ENGAGEMENT_TICK_MS = 15000;
const ENGAGEMENT_CAP_MS = 5 * 60 * 1000;
let engagementTimer = null;
let engagementState = null;

const stopEngagementTicker = () => {
  if (engagementTimer != null) {
    clearInterval(engagementTimer);
    engagementTimer = null;
  }
};

const tickEngagement = () => {
  if (!engagementState) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    return;
  }
  if (engagementState.engagedMs >= ENGAGEMENT_CAP_MS) {
    stopEngagementTicker();
    return;
  }
  engagementState.engagedMs += ENGAGEMENT_TICK_MS;
  try {
    trackEvent('page_engaged', {
      path: engagementState.path,
      engaged_ms: engagementState.engagedMs,
    });
  } catch {
    // ignore
  }
};

const startEngagementForCurrentPath = () => {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (!isTrackablePath(path)) {
    stopEngagementTicker();
    engagementState = null;
    return;
  }
  if (engagementState && engagementState.path === path) return;
  engagementState = {path, engagedMs: 0};
  stopEngagementTicker();
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
    return;
  }
  engagementTimer = setInterval(tickEngagement, ENGAGEMENT_TICK_MS);
};

const bindEngagementHeartbeat = () => {
  if (typeof window === 'undefined') return;
  if (window.__emblemEngagementBound) return;
  window.__emblemEngagementBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (!engagementTimer && engagementState) {
        engagementTimer = setInterval(tickEngagement, ENGAGEMENT_TICK_MS);
      }
    } else {
      stopEngagementTicker();
    }
  });
  window.addEventListener('pagehide', stopEngagementTicker);
  // Watch URL changes (React-Router) — same lightweight poll as scroll.
  let lastPath = window.location.pathname;
  startEngagementForCurrentPath();
  setInterval(() => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      startEngagementForCurrentPath();
    }
  }, 1000);
};

// ─── Sentry-lite: capture JS errors into trackWebsiteEvent ──────
// window.onerror + unhandledrejection both flow into the same analytics
// pipeline so the admin can see real client-side errors next to traffic
// data without paying for a separate vendor. Each error is normalized,
// truncated, and rate-limited per page-load to keep the payload sane.
const ERROR_CAPTURE_BUDGET = 10;
let errorCaptureCount = 0;

const captureClientError = (kind, payload = {}) => {
  if (errorCaptureCount >= ERROR_CAPTURE_BUDGET) return;
  errorCaptureCount += 1;
  try {
    const truncate = (value, max = 500) =>
      String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, max);
    trackEvent('js_error', {
      kind: truncate(kind, 40),
      message: truncate(payload.message, 240),
      source: truncate(payload.source, 240),
      line: Number.isFinite(payload.line) ? payload.line : 0,
      column: Number.isFinite(payload.column) ? payload.column : 0,
      stack: truncate(payload.stack, 500),
    });
  } catch {
    // Never let analytics throw inside an error handler.
  }
};

const bindJsErrorCapture = () => {
  if (typeof window === 'undefined') return;
  if (window.__emblemJsErrorBound) return;
  window.__emblemJsErrorBound = true;

  window.addEventListener('error', (event) => {
    captureClientError('window_error', {
      message: event?.message,
      source: event?.filename,
      line: event?.lineno,
      column: event?.colno,
      stack: event?.error?.stack,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    captureClientError('unhandled_rejection', {
      message:
        typeof reason === 'string'
          ? reason
          : reason?.message || 'Unhandled promise rejection',
      stack: reason?.stack,
    });
  });
};

// ─── Web Vitals → trackWebsiteEvent ─────────────────────────────
// LCP / CLS / INP / FCP / TTFB go through the same analytics
// pipeline as everything else so the admin gets a perf tab for free.
// We use the Performance Observer API directly (no extra dependency)
// for the metrics it natively reports; the script is no-op on browsers
// that don't support the relevant entry types.
let webVitalsBound = false;

const reportWebVital = (name, value, extra = {}) => {
  if (!Number.isFinite(value)) return;
  try {
    trackEvent('web_vital', {
      name,
      value: Math.round(value * 100) / 100,
      ...extra,
    });
  } catch {
    // ignore
  }
};

const bindWebVitalsCapture = () => {
  if (typeof window === 'undefined') return;
  if (webVitalsBound) return;
  if (typeof PerformanceObserver === 'undefined') return;
  webVitalsBound = true;

  const safeObserve = (entryType, handler) => {
    try {
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach(handler);
      });
      observer.observe({type: entryType, buffered: true});
      return observer;
    } catch {
      return null;
    }
  };

  // LCP: largest contentful paint (lower = better, target < 2.5s)
  let lastLcp = 0;
  safeObserve('largest-contentful-paint', (entry) => {
    lastLcp = entry?.renderTime || entry?.startTime || lastLcp;
  });

  // CLS: cumulative layout shift (lower = better, target < 0.1)
  let clsValue = 0;
  safeObserve('layout-shift', (entry) => {
    if (entry && !entry.hadRecentInput && Number.isFinite(entry.value)) {
      clsValue += entry.value;
    }
  });

  // FCP: first contentful paint
  safeObserve('paint', (entry) => {
    if (entry?.name === 'first-contentful-paint') {
      reportWebVital('FCP', entry.startTime);
    }
  });

  // INP-ish: longest interaction event (>40ms threshold to filter noise)
  let worstInteraction = 0;
  safeObserve('event', (entry) => {
    if (
      entry &&
      Number.isFinite(entry.duration) &&
      entry.duration > 40 &&
      entry.duration > worstInteraction
    ) {
      worstInteraction = entry.duration;
    }
  });

  // Flush LCP / CLS / INP on tab hide so we capture the *final* values
  // rather than the in-progress ones.
  const flush = () => {
    if (lastLcp > 0) reportWebVital('LCP', lastLcp);
    if (clsValue > 0) reportWebVital('CLS', clsValue);
    if (worstInteraction > 0) reportWebVital('INP', worstInteraction);
  };
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') flush();
    },
    {once: true},
  );
  window.addEventListener('pagehide', flush, {once: true});
};

export const trackEvent = (eventName, params = {}) => {
  if (
    typeof window === 'undefined' ||
    isInternalTrafficDisabled() ||
    isAnalyticsBlockedByConsent()
  ) {
    return;
  }

  const payload = buildEventPayload(eventName, params);
  if (!isTrackablePath(payload.pagePath)) {
    return;
  }

  ensureSessionStartTracked({
    pathname: payload.pagePath,
    search: payload.pageSearch,
  });
  sendGaEvent(eventName, params);

  if (shouldSkipDuplicateEvent(eventName, payload)) {
    return;
  }

  postWebsiteEvent(payload);
};

export const trackPageView = (path) => {
  if (typeof window === 'undefined' || isInternalTrafficDisabled()) {
    return;
  }

  const route = parsePathInput(path);
  if (!isTrackablePath(route.pathname)) {
    return;
  }

  ensureSessionStartTracked(route);

  const payload = buildEventPayload(
    'page_view',
    {
      page_path: route.pathname,
    },
    route,
  );

  // Dedup before BOTH GA and Firestore writes, otherwise the dashboard
  // and GA4 each see the duplicate and we end up double-counting.
  if (shouldSkipDuplicateEvent('page_view', payload)) {
    return;
  }

  sendGaEvent('page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });

  postWebsiteEvent(payload);
};

export const trackStoreClick = ({ platform, placement, href }) => {
  trackEvent('store_click', {
    platform,
    placement,
    href
  });
};

export const trackLeadSubmit = ({ formType, status }) => {
  trackEvent('lead_submit', {
    form_type: formType,
    status
  });
};

/*
 * Enriches store-link clicks with attribution data so the Flutter app
 * can join the resulting install back to the originating web visitor.
 *
 * - Android: Google Play preserves the `referrer` query parameter all
 *   the way into the Play Install Referrer API on first launch. We
 *   pack visitor_id / utm_source / utm_medium / utm_campaign into the
 *   referrer so the app can retrieve them via the
 *   `play_install_referrer` Flutter plugin.
 *
 * - iOS: App Store doesn't preserve query strings. The Flutter app
 *   instead posts a candidate fingerprint on first launch which the
 *   `recordAppInstall` cloud function matches against recent
 *   `store_click` events for the same country/device combo. We still
 *   tag the URL with `pcid=<visitorId>` so server logs can correlate
 *   if a deep link survives.
 */
const buildAndroidReferrerString = ({visitorId, attribution}) => {
  const parts = [];
  const push = (key, value) => {
    if (!value) return;
    parts.push(`${key}=${encodeURIComponent(value)}`);
  };
  push('visitor_id', visitorId);
  push(
    'utm_source',
    attribution?.lastTouchUtmSource || attribution?.firstTouchUtmSource || '',
  );
  push(
    'utm_medium',
    attribution?.lastTouchUtmMedium || attribution?.firstTouchUtmMedium || '',
  );
  push(
    'utm_campaign',
    attribution?.lastTouchUtmCampaign ||
      attribution?.firstTouchUtmCampaign ||
      '',
  );
  push(
    'utm_content',
    attribution?.lastTouchUtmContent ||
      attribution?.firstTouchUtmContent ||
      '',
  );
  return parts.join('&');
};

const enrichStoreHref = (rawHref, platform) => {
  try {
    const url = new URL(rawHref, window.location.origin);
    const visitorId = getVisitorId();
    const attribution = getLeadAttributionPayload();
    if (platform === 'android') {
      const referrer = buildAndroidReferrerString({visitorId, attribution});
      if (referrer) {
        url.searchParams.set('referrer', referrer);
      }
    } else if (platform === 'ios') {
      // App Store strips most query strings, but the click URL survives
      // in the user's clipboard / Safari handoff sometimes — tag it
      // so server logs can correlate when it does.
      if (visitorId) url.searchParams.set('pcid', visitorId);
    }
    return url.toString();
  } catch {
    return rawHref;
  }
};

export const bindStoreLinkTracking = () => {
  if (typeof document === 'undefined' || storeTrackingBound) {
    return;
  }

  storeTrackingHandler = (event) => {
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) {
      return;
    }

    const href = anchor.getAttribute('href') || '';
    if (!href.includes('apps.apple.com') && !href.includes('play.google.com/store/apps')) {
      return;
    }

    const platform = href.includes('play.google.com/store/apps') ? 'android' : 'ios';
    const placement =
      anchor.dataset.trackPlacement ||
      anchor.getAttribute('aria-label') ||
      anchor.className ||
      'store_link';

    // Rewrite the anchor with attribution params before the browser
    // navigates. Skip if the visitor declined consent so we don't pass
    // their visitorId off-site.
    if (!isAnalyticsBlockedByConsent()) {
      const enrichedHref = enrichStoreHref(href, platform);
      if (enrichedHref && enrichedHref !== href) {
        anchor.setAttribute('href', enrichedHref);
      }
    }

    trackStoreClick({ platform, placement, href });
  };

  document.addEventListener('click', storeTrackingHandler);
  storeTrackingBound = true;
};

export const getWebsiteAnalyticsVisitorId = () => getVisitorId();

export const isWebsiteAnalyticsIgnoredForCurrentBrowser = () =>
  isInternalTrafficDisabled();

export const setWebsiteAnalyticsIgnoredForCurrentBrowser = (ignored) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  writeStorageValue(
    window.localStorage,
    INTERNAL_TRAFFIC_DISABLED_STORAGE_KEY,
    ignored ? '1' : '0'
  );

  return isInternalTrafficDisabled();
};
